import {merge} from 'lodash';

import {captureMessage} from '@sentry/browser';

import {AppConfig, AppProfile} from '../types';

type ClientFetch = typeof fetch;

function authedOrAnonUrl(requiresAuth: boolean, url: string) {
  const apiMatcher = /^\/?api\/(anon\/)?(.*)$/;

  if (requiresAuth) {
    return url.replace(apiMatcher, '/api/$2');
  }

  return url.replace(apiMatcher, '/api/anon/$2');
}

export function authedOrAnonRequest(
  requiresAuth: boolean,
  input: RequestInfo | URL,
): RequestInfo | URL {
  if (typeof input === 'string') {
    return authedOrAnonUrl(requiresAuth, input);
  }

  if ('url' in input) {
    return {
      ...input,
      url: authedOrAnonUrl(requiresAuth, input.url),
    };
  }

  // this shouldn't ever happen, but if it ever did I'd want to know
  captureMessage(
    "ApiProvider expected an api endpoint url, but didn't find one",
    {level: 'warning'},
  );

  return input;
}

export function wrapFetch(
  fetch: ClientFetch,
  getConfig: () => AppConfig,
  getProfile: () => AppProfile,
  onAuthFailure?: () => void,
): ClientFetch {
  const wrappedFetch: ClientFetch = async (req, init?) => {
    const config = getConfig();
    const {locale} = config;
    const profile = getProfile();
    const {livemode, visitor} = profile;
    const requiresAuth = visitor.sessionType !== 'anonymous';

    const reqWithAnonRoutes = authedOrAnonRequest(requiresAuth, req);

    // stripe-livemode is used by manage
    const livemodeHeaders = {'stripe-livemode': livemode ? 'true' : 'false'};

    const localeHeaders = {
      'Accept-Language': [locale, ...navigator.languages],
    };

    const headers = {
      ...livemodeHeaders,
      ...localeHeaders,
    };

    const mergedInit = merge({headers}, init);

    const response = await fetch(reqWithAnonRoutes, mergedInit);
    if (response.status === 401 && requiresAuth && onAuthFailure) {
      onAuthFailure();
    }
    return response;
  };
  return wrappedFetch;
}
